/**
 * VistoeraTASK-00562 — one shared, per-tab device-account list for every
 * `TenantAccountMenu` instance (workbench pages + Canvas).
 *
 * - Fetched only when a menu is opened (never on render, never polled).
 * - De-duplicated: concurrent opens share one in-flight request; a fresh
 *   cached list (<= STALE_MS) is reused when the menu is re-opened or when the
 *   user navigates between pages (a new page's menu no longer re-requests).
 * - Owned by one user id: a different identity can never see another
 *   identity's cached list (the server also computes `is_current` per caller).
 * - A failed load is an explicit `error` state — it is NOT collapsed into an
 *   empty "no other accounts" list, so the UI can show retry.
 * - Cleared on every account switch via `invalidateDeviceAccounts()`.
 */
import type { DeviceAccountOut } from '../api'

export type DeviceAccountsStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface DeviceAccountsSnapshot {
  status: DeviceAccountsStatus
  ownerUserId: string | null
  accounts: DeviceAccountOut[]
  loadedAt: number
}

export const DEVICE_ACCOUNTS_STALE_MS = 15_000

const EMPTY: DeviceAccountsSnapshot = { status: 'idle', ownerUserId: null, accounts: [], loadedAt: 0 }

let snapshot: DeviceAccountsSnapshot = EMPTY
let inflight: { userId: string; promise: Promise<void> } | null = null
let generation = 0
const listeners = new Set<() => void>()

function publish(next: DeviceAccountsSnapshot): void {
  snapshot = next
  listeners.forEach((listener) => listener())
}

export function getDeviceAccountsSnapshot(): DeviceAccountsSnapshot {
  return snapshot
}

export function subscribeDeviceAccounts(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Drop everything (account switch / logout). Late responses are ignored. */
export function invalidateDeviceAccounts(): void {
  generation += 1
  inflight = null
  publish(EMPTY)
}

/** Replace the cached list with a server-returned one (e.g. after add-account). */
export function setDeviceAccounts(userId: string, accounts: DeviceAccountOut[], now: number = Date.now()): void {
  publish({ status: 'ready', ownerUserId: userId, accounts, loadedAt: now })
}

/**
 * VistoeraTASK-00586 — after nickname save, patch the current row in the
 * shared cache so the account switcher updates immediately. Never re-adds
 * a removed account; only mutates an existing `user_id` match.
 */
export function patchCurrentAccountDisplayName(userId: string, displayName: string): void {
  if (snapshot.ownerUserId !== userId || snapshot.accounts.length === 0) return
  const next = snapshot.accounts.map((account) =>
    account.user_id === userId ? { ...account, display_name: displayName } : account,
  )
  publish({ ...snapshot, accounts: next, loadedAt: Date.now() })
}

export interface LoadDeviceAccountsOptions {
  force?: boolean
  now?: number
}

/**
 * Load (or reuse) the device-account list for `userId`.
 * Never throws: failures become `status: 'error'`.
 *
 * `force: true` (used after remove/add) always starts a new fetch and abandons
 * any in-flight request for the same owner, so a pre-remove response cannot
 * overwrite a post-remove list (VistoeraTASK-00580).
 */
export function loadDeviceAccounts(
  userId: string,
  fetcher: () => Promise<DeviceAccountOut[]>,
  options: LoadDeviceAccountsOptions = {},
): Promise<void> {
  const now = options.now ?? Date.now()
  const sameOwner = snapshot.ownerUserId === userId

  if (!options.force && sameOwner && snapshot.status === 'ready' && now - snapshot.loadedAt < DEVICE_ACCOUNTS_STALE_MS) {
    return Promise.resolve()
  }
  if (!options.force && inflight && inflight.userId === userId) return inflight.promise

  // Force: abandon any in-flight so a pre-remove response cannot overwrite us.
  if (options.force) {
    generation += 1
    inflight = null
  }

  const myGeneration = generation
  // Keep the last good list for the SAME owner while revalidating; never another owner's.
  publish({
    status: 'loading',
    ownerUserId: userId,
    accounts: sameOwner ? snapshot.accounts : [],
    loadedAt: sameOwner ? snapshot.loadedAt : 0,
  })

  const promise = fetcher().then(
    (accounts) => {
      if (myGeneration !== generation) return
      inflight = null
      publish({ status: 'ready', ownerUserId: userId, accounts, loadedAt: Date.now() })
    },
    () => {
      if (myGeneration !== generation) return
      inflight = null
      publish({
        status: 'error',
        ownerUserId: userId,
        accounts: snapshot.ownerUserId === userId ? snapshot.accounts : [],
        loadedAt: 0,
      })
    },
  )
  inflight = { userId, promise }
  return promise
}

/** Drop one account from the cached list immediately (after a successful remove). */
export function removeDeviceAccountFromCache(userId: string, removedUserId: string): void {
  if (snapshot.ownerUserId !== userId) return
  if (!snapshot.accounts.some((a) => a.user_id === removedUserId)) return
  publish({
    status: 'ready',
    ownerUserId: userId,
    accounts: snapshot.accounts.filter((a) => a.user_id !== removedUserId),
    loadedAt: Date.now(),
  })
}
